import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Checkbox,
  Tooltip,
  useTheme,
  Link as MuiLink,
} from '@mui/material';
import { ArrowBack, Close, DeleteForever, Download, InfoOutlined, QrCode2, RemoveCircleOutline, Clear, CheckCircle, Cancel, ShoppingCart } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faUpload, faFileCode } from '@fortawesome/free-solid-svg-icons';
import Editor from '@monaco-editor/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router';
import { useSnackbar } from 'notistack';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import QRCode from 'react-qr-code';
import { useApplicationContext } from 'ApplicationContext';
import AccessDenied from 'Components/AccessDenied';
import { NotFound } from 'Pages/Public/NotFound';
import {
  CreateFileTypeLicenseRequest,
  DeviceQueryModel,
  DeviceStatusEnum,
  DeviceLicenseCollectionResponse,
  DeviceModuleOptionOverview,
  EUserGroup,
  FileTypeLicenseOptionResponse,
  FileTypeLicenseResponse,
  FileTypeLicenseStatus,
  GetFileTypeData,
  LicenseOptionValue,
  LicenseOptionType,
  HostIdResponse,
} from 'apiClient/Api';
import { useDocumentTitle } from 'Hooks/useDocumentTitle';

const getOptionKey = (option: FileTypeLicenseOptionResponse): string =>
  option.licenseOption?.identifier ?? option.licenseOptionId ?? option.id ?? '';

const getOptionDefaultValue = (option: FileTypeLicenseOptionResponse): string =>
  option.fileTypeSpecificDefault ?? option.licenseOption?.defaultValue ?? '';

const normalizeOptionValue = (type: LicenseOptionType | undefined, value?: string | null) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  switch (type) {
    case LicenseOptionType.Boolean:
      return value === 'true' || value === '1';
    case LicenseOptionType.Number: {
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? value : numberValue;
    }
    case LicenseOptionType.Date:
    case LicenseOptionType.String:
    default:
      return value;
  }
};

const getStatusChipColor = (status?: FileTypeLicenseStatus): 'success' | 'warning' | 'error' | 'default' | 'info' => {
  switch (status) {
    case FileTypeLicenseStatus.Requested:
      return 'warning';
    case FileTypeLicenseStatus.Approved:
      return 'info';
    case FileTypeLicenseStatus.Active:
      return 'success';
    case FileTypeLicenseStatus.Expired:
      return 'error';
    case FileTypeLicenseStatus.Rejected:
      return 'error';
    default:
      return 'default';
  }
};

const getEffectiveStatus = (
  status: FileTypeLicenseStatus | undefined,
  expiresAt?: string | null,
): FileTypeLicenseStatus | undefined => {
  if (expiresAt && dayjs(expiresAt).isBefore(dayjs(), 'day')) {
    return FileTypeLicenseStatus.Expired;
  }

  return status;
};

const getStatusLabel = (status: FileTypeLicenseStatus | undefined, t: (key: string) => string): string => {
  switch (status) {
    case FileTypeLicenseStatus.Requested:
      return t('deviceManagement.licensePage.status.requested');
    case FileTypeLicenseStatus.Approved:
      return t('deviceManagement.licensePage.status.approved');
    case FileTypeLicenseStatus.Active:
      return t('deviceManagement.licensePage.status.active');
    case FileTypeLicenseStatus.Expired:
      return t('deviceManagement.licensePage.status.expired');
    case FileTypeLicenseStatus.Rejected:
      return t('deviceManagement.licensePage.status.rejected');
    case FileTypeLicenseStatus.Draft:
      return t('deviceManagement.licensePage.status.draft');
    default:
      return '-';
  }
};

const formatDate = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  return dayjs(value).format('DD.MM.YYYY');
};

/**
 * Returns the updates end date label based on registration date and months.
 */
const getUpdatesUntilLabel = (registrationDate?: string | null, updatesMonths?: number | null): string => {
  if (!registrationDate || !updatesMonths) {
    return '-';
  }

  return dayjs(registrationDate).add(updatesMonths, 'month').format('DD.MM.YYYY');
};

/**
 * Returns the updates label: months for non-active devices, or end date for active ones.
 */
const getUpdatesInfoLabel = (
  status?: DeviceStatusEnum,
  updatesMonths?: number | null,
  registrationDate?: string | null,
): string => {
  if (status === DeviceStatusEnum.Active) {
    return getUpdatesUntilLabel(registrationDate, updatesMonths);
  }

  return updatesMonths != null ? String(updatesMonths) : '-';
};

/**
 * Formats option values for display.
 */
const formatOptionValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch (err) {
    return String(value);
  }
};
const normalizeStatusValue = (status: FileTypeLicenseStatus | string | null | undefined): FileTypeLicenseStatus | undefined => {
  if (status === null || status === undefined) {
    return undefined;
  }
  if (typeof status === 'number') {
    return status;
  }
  const mapped = (FileTypeLicenseStatus as unknown as Record<string, number>)[status];
  return typeof mapped === 'number' ? mapped : undefined;
};

const buildCollectionFileName = (deviceId?: string | null): string => {
  const sanitize = (value?: string | null) => (value ?? 'unknown').replace(/[^a-zA-Z0-9-_]+/g, '_');
  const date = dayjs().format('YYYYMMDD');
  return `license_collection_${sanitize(deviceId)}_${date}.lic`;
};

const QR_MAX_LENGTH = 1800;

const DeviceLicensePageContent: React.FC = () => {
  useDocumentTitle('deviceManagement.manageLicenses');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { deviceId } = useParams<{ deviceId: string }>();
  const context = useApplicationContext();
  const theme = useTheme();

  const [licenses, setLicenses] = useState<FileTypeLicenseResponse[]>([]);
  const [licensesLoading, setLicensesLoading] = useState<boolean>(false);
  const [licensesError, setLicensesError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState<boolean>(false);
  const [deviceDetails, setDeviceDetails] = useState<DeviceQueryModel | null>(null);
  const [deviceDetailsLoading, setDeviceDetailsLoading] = useState<boolean>(false);
  const [deviceDetailsError, setDeviceDetailsError] = useState<string | null>(null);
  const [deviceNotFound, setDeviceNotFound] = useState<boolean>(false);
  
  // File upload for XML
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  // Assign license states
  const [availableFileTypes, setAvailableFileTypes] = useState<GetFileTypeData[]>([]);
  const [fileTypesLoading, setFileTypesLoading] = useState<boolean>(false);
  const [selectedFileType, setSelectedFileType] = useState<GetFileTypeData | null>(null);
  const [licenseOptions, setLicenseOptions] = useState<FileTypeLicenseOptionResponse[]>([]);
  const [licenseOptionsLoading, setLicenseOptionsLoading] = useState<boolean>(false);
  const [licenseOptionValues, setLicenseOptionValues] = useState<Record<string, string>>({});
  const [assignExpiresAt, setAssignExpiresAt] = useState<string>('');
  const [assignNotes, setAssignNotes] = useState<string>('');
  const [assigning, setAssigning] = useState<boolean>(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  // Collection states
  const [collectionData, setCollectionData] = useState<DeviceLicenseCollectionResponse | null>(null);
  const [collectionLoading, setCollectionLoading] = useState<boolean>(false);
  const [collectionError, setCollectionError] = useState<string | null>(null);
  const [collectionOpen, setCollectionOpen] = useState<boolean>(false);
  const [downloadingCollection, setDownloadingCollection] = useState<boolean>(false);

  // HostID check states
  const [hostIdCheckData, setHostIdCheckData] = useState<HostIdResponse | null>(null);
  const [hostIdCheckLoading, setHostIdCheckLoading] = useState<boolean>(false);
  const [hostIdCheckError, setHostIdCheckError] = useState<string | null>(null);

  // Dialog states
  const [revokeTarget, setRevokeTarget] = useState<FileTypeLicenseResponse | null>(null);
  const [revoking, setRevoking] = useState<boolean>(false);
  const [detailLicense, setDetailLicense] = useState<FileTypeLicenseResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FileTypeLicenseResponse | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [approveTarget, setApproveTarget] = useState<FileTypeLicenseResponse | null>(null);
  const [approving, setApproving] = useState<boolean>(false);
  const [rejectTarget, setRejectTarget] = useState<FileTypeLicenseResponse | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [rejecting, setRejecting] = useState<boolean>(false);
  const [submitTarget, setSubmitTarget] = useState<FileTypeLicenseResponse | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitAllOpen, setSubmitAllOpen] = useState<boolean>(false);
  const [submittingAll, setSubmittingAll] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // QR Code states
  const [qrOpen, setQrOpen] = useState<boolean>(false);
  const [qrValue, setQrValue] = useState<string>('');
  const [qrLoading, setQrLoading] = useState<boolean>(false);
  const [qrError, setQrError] = useState<string | null>(null);

  // XML License states
  const [hasLicenseXml, setHasLicenseXml] = useState<boolean>(false);
  const [xmlDialogOpen, setXmlDialogOpen] = useState<boolean>(false);
  const [xmlContent, setXmlContent] = useState<string>('');
  const [xmlLoading, setXmlLoading] = useState<boolean>(false);
  const [xmlError, setXmlError] = useState<string | null>(null);

  // Snackbar for notifications
  const { enqueueSnackbar } = useSnackbar();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && deviceId) {
      handleFileUpload(file, deviceId);
    }
  };

  const handleFileUpload = async (file: File, deviceId: string) => {
    try {
      // Note: The API expects 'xmlFile' as the field name for the form file
      await context.api.device.deviceUploadLicenseXml(deviceId, { xmlFile: file } as any);
      enqueueSnackbar(t('deviceManagement.licensePage.upload.success'), { variant: 'success' });
      // Refresh licenses after successful upload
      await loadLicenses();
    } catch (error) {
      console.error('File upload failed', error);
      enqueueSnackbar(t('deviceManagement.licensePage.upload.error'), { variant: 'error' });
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  /**
   * Check if device has license XML
   */
  const checkHasLicenseXml = useCallback(async () => {
    if (!deviceId) {
      setHasLicenseXml(false);
      return;
    }

    try {
      const response = await context.api.device.deviceHasIoeServerConfigXml(deviceId);
      setHasLicenseXml(response.data ?? false);
    } catch (err) {
      setHasLicenseXml(false);
    }
  }, [context.api.device, deviceId]);

  /**
   * Open XML dialog and load content
   */
  const handleOpenXmlDialog = useCallback(async () => {
    if (!deviceId) return;

    setXmlDialogOpen(true);
    setXmlLoading(true);
    setXmlError(null);
    setXmlContent('');

    try {
      const response = await context.api.device.deviceDownloadIoeServerConfigXml(deviceId, {
        format: 'text',
      });
      setXmlContent(response.data as unknown as string || '');
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.title || err?.message || t('error.fetchFailed');
      setXmlError(errorMessage);
    } finally {
      setXmlLoading(false);
    }
  }, [context.api.device, deviceId, t]);

  /**
   * Close XML dialog
   */
  const handleCloseXmlDialog = useCallback(() => {
    setXmlDialogOpen(false);
    setXmlContent('');
    setXmlError(null);
  }, []);

  /**
   * Download XML file
   */
  const handleDownloadXml = useCallback(async () => {
    if (!deviceId || !xmlContent) return;

    try {
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `license_${deviceId}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      enqueueSnackbar(t('deviceManagement.licensePage.xml.downloadSuccess'), { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(t('deviceManagement.licensePage.xml.downloadError'), { variant: 'error' });
    }
  }, [deviceId, xmlContent, enqueueSnackbar, t]);

  const loadLicenses = useCallback(async () => {
    if (!deviceId) {
      setLicenses([]);
      return;
    }

    setLicensesLoading(true);
    setLicensesError(null);
    setAccessDenied(false);
    try {
      const response = await context.api.api.licenseGetDeviceLicenses(deviceId);
      setLicenses(response.data ?? []);
    } catch (err) {
      const statusCode = (err as { response?: { status?: number } })?.response?.status;
      if (statusCode === 403) {
        setAccessDenied(true);
        setLicensesError(null);
        setLicenses([]);
      } else {
        setLicensesError(t('error.fetchFailed'));
      }
    } finally {
      setLicensesLoading(false);
    }
  }, [context.api.api, deviceId, t]);

  /**
   * Loads device details for the current device id.
   */
  const loadDeviceDetails = useCallback(async () => {
    if (!deviceId) {
      setDeviceDetails(null);
      return;
    }

    setDeviceDetailsLoading(true);
    setDeviceDetailsError(null);
    setDeviceNotFound(false);
    try {
      const response = await context.api.device.deviceGetDevice(deviceId);
      setDeviceDetails(response.data as DeviceQueryModel);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setDeviceNotFound(true);
      } else {
        setDeviceDetailsError(t('error.fetchFailed'));
      }
    } finally {
      setDeviceDetailsLoading(false);
    }
  }, [context.api.device, deviceId, t]);

  const loadFileTypes = useCallback(async () => {
    setFileTypesLoading(true);
    setAssignError(null);
    try {
      const response = await context.api.fileType.fileTypeGetFileTypesPublic();
      setAvailableFileTypes(response.data ?? []);
    } catch (err) {
      setAssignError(t('error.fetchFailed'));
    } finally {
      setFileTypesLoading(false);
    }
  }, [context.api.fileType, t]);

  const loadLicenseOptions = useCallback(
    async (fileTypeId: string) => {
      setLicenseOptionsLoading(true);
      setAssignError(null);
      try {
        const response = await context.api.api.licenseGetFileTypeLicenseOptions(fileTypeId);
        const options = response.data ?? [];
        setLicenseOptions(options);

        const initialValues: Record<string, string> = {};
        options.forEach((option) => {
          const defaultValue = getOptionDefaultValue(option);
          if (defaultValue) {
            initialValues[getOptionKey(option)] = String(defaultValue);
          }
        });
        setLicenseOptionValues(initialValues);
      } catch (err) {
        setAssignError(t('error.fetchFailed'));
      } finally {
        setLicenseOptionsLoading(false);
      }
    },
    [context.api.api, t],
  );

  const loadCollection = useCallback(async () => {
    if (!deviceId) {
      setCollectionData(null);
      return;
    }

    setCollectionLoading(true);
    setCollectionError(null);
    try {
      const response = await context.api.api.licenseGetDeviceLicenseCollection(deviceId);
      setCollectionData(response.data ?? null);
    } catch (err) {
      setCollectionError(t('error.fetchFailed'));
    } finally {
      setCollectionLoading(false);
    }
  }, [context.api.api, deviceId, t]);

  /**
   * Checks the HostID validity using the API
   */
  const checkHostId = useCallback(async (hostId: string) => {
    if (!hostId || hostId.length < 5) {
      return;
    }

    setHostIdCheckLoading(true);
    setHostIdCheckError(null);
    try {
      const response = await context.api.api.hostIdCheckHostId(hostId);
      setHostIdCheckData(response.data ?? null);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.title || err?.message || t('error.fetchFailed');
      setHostIdCheckError(errorMessage);
    } finally {
      setHostIdCheckLoading(false);
    }
  }, [context.api.api, t]);

  useEffect(() => {
    loadFileTypes();
  }, [loadFileTypes]);

  useEffect(() => {
    loadLicenses();
  }, [loadLicenses]);

  useEffect(() => {
    loadDeviceDetails();
  }, [loadDeviceDetails]);

  useEffect(() => {
    checkHasLicenseXml();
  }, [checkHasLicenseXml]);

  /**
   * Auto-check HostID when device details are loaded
   */
  useEffect(() => {
    if (deviceDetails?.deviceId && deviceDetails.deviceId.length >= 5) {
      checkHostId(deviceDetails.deviceId);
    }
  }, [deviceDetails?.deviceId, checkHostId]);


  /**
   * Resolves an option label for a given identifier.
   */
  const resolveOptionLabel = useCallback((identifier: string, moduleOptions?: DeviceModuleOptionOverview[]) => {
    if (!moduleOptions) {
      return identifier;
    }

    return moduleOptions.find((option) => option.identifier === identifier)?.commonName ?? identifier;
  }, []);

  /**
   * Formats option values with localized boolean labels.
   */
  const formatOptionValueLabel = useCallback(
    (value: unknown) => {
      if (typeof value === 'boolean') {
        return value ? t('yes') : t('no');
      }
      return formatOptionValue(value);
    },
    [t],
  );

  const handleFileTypeChange = (fileTypeId: string) => {
    const fileType = availableFileTypes.find((ft) => ft.id === fileTypeId);
    setSelectedFileType(fileType ?? null);
    setLicenseOptionValues({});

    if (fileType?.id) {
      loadLicenseOptions(fileType.id);
    } else {
      setLicenseOptions([]);
    }
  };

  const handleOptionValueChange = (option: FileTypeLicenseOptionResponse, value: string) => {
    const optionKey = getOptionKey(option);
    if (!optionKey) {
      return;
    }

    setLicenseOptionValues((prev) => ({
      ...prev,
      [optionKey]: value,
    }));
  };

  const handleAssignLicense = async () => {
    if (!deviceId || !selectedFileType?.id) {
      setAssignError(t('deviceManagement.licenses.errors.missingData'));
      return;
    }

    const missingRequired = licenseOptions.some((option) => {
      const optionKey = getOptionKey(option);
      const value = licenseOptionValues[optionKey];
      return option.isRequired && (value === undefined || value === null || value.trim() === '');
    });

    if (missingRequired) {
      setAssignError(t('deviceManagement.licenses.errors.missingRequired'));
      return;
    }

    const licenseOptionsPayload = licenseOptions.reduce<LicenseOptionValue[]>((acc, option) => {
      const identifier = option.licenseOption?.identifier;
      const optionKey = getOptionKey(option);
      const rawValue = licenseOptionValues[optionKey];
      if (!identifier) {
        return acc;
      }

      const normalizedValue = normalizeOptionValue(option.licenseOption?.type, rawValue);
      if (normalizedValue === null || normalizedValue === undefined) {
        return acc;
      }

      acc.push({
        identifier,
        value: normalizedValue,
      });
      return acc;
    }, []);

    const payload: CreateFileTypeLicenseRequest = {
      fileTypeId: selectedFileType.id,
      expiresAt: assignExpiresAt ? new Date(assignExpiresAt).toISOString() : undefined,
      notes: assignNotes.trim() ? assignNotes.trim() : undefined,
      licenseOptions: licenseOptionsPayload.length > 0 ? licenseOptionsPayload : undefined,
    };

    setAssigning(true);
    setAssignError(null);
    try {
      if (context.isInternal) {
        await context.api.api.licenseCreateDeviceLicense(deviceId, payload);
      } else {
        await context.api.api.licenseCreateDraftLicense(deviceId, payload);
      }
      await loadLicenses();
      setSelectedFileType(null);
      setLicenseOptions([]);
      setLicenseOptionValues({});
      setAssignExpiresAt('');
      setAssignNotes('');
    } catch (err) {
      setAssignError(t('error.fetchFailed'));
    } finally {
      setAssigning(false);
    }
  };

  const handleOpenRevokeDialog = useCallback((license: FileTypeLicenseResponse) => {
    setRevokeTarget(license);
  }, []);

  const handleCloseRevokeDialog = useCallback(() => {
    setRevokeTarget(null);
  }, []);

  const handleConfirmRevoke = async () => {
    if (!deviceId || !revokeTarget?.id) {
      return;
    }

    setRevoking(true);
    try {
      await context.api.api.licenseRevokeDeviceLicense(deviceId, revokeTarget.id);
      await loadLicenses();
      setRevokeTarget(null);
    } catch (err) {
      setLicensesError(t('error.fetchFailed'));
    } finally {
      setRevoking(false);
    }
  };

  const handleOpenDetails = useCallback((license: FileTypeLicenseResponse) => {
    setDetailLicense(license);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailLicense(null);
  }, []);

  const handleOpenCollection = useCallback(() => {
    setCollectionOpen(true);
  }, []);

  const handleCloseCollection = useCallback(() => {
    setCollectionOpen(false);
  }, []);

  const handleDownloadCollection = useCallback(async () => {
    if (!deviceId) {
      return;
    }

    setDownloadingCollection(true);
    setDownloadError(null);
    try {
      const response = await context.api.api.licenseDownloadDeviceLicenseCollection(deviceId, {
        format: 'blob',
      });
      const responseData = response.data as unknown;
      const blob =
        responseData instanceof Blob
          ? responseData
          : new Blob([responseData as BlobPart], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', buildCollectionFileName(deviceId));
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(t('error.fetchFailed'));
    } finally {
      setDownloadingCollection(false);
    }
  }, [context.api.api, deviceId, t]);

  const fetchCollectionLicenseText = useCallback(async () => {
    if (!deviceId) {
      return '';
    }

    const response = await context.api.api.licenseDownloadDeviceLicenseCollection(deviceId, {
      format: 'blob',
    });
    const responseData = response.data as unknown;
    if (typeof responseData === 'string') {
      return responseData;
    }
    if (responseData instanceof Blob) {
      return await responseData.text();
    }
    if (responseData instanceof ArrayBuffer) {
      return new TextDecoder().decode(new Uint8Array(responseData));
    }
    return '';
  }, [context.api.api, deviceId]);

  const handleOpenQr = useCallback(async () => {
    setQrOpen(true);
    setQrLoading(true);
    setQrError(null);
    try {
      const text = await fetchCollectionLicenseText();
      if (text.length > QR_MAX_LENGTH) {
        setQrValue('');
        setQrError(t('deviceManagement.licensePage.collection.qrTooLarge'));
      } else {
        setQrValue(text);
      }
    } catch (err) {
      setQrError(t('error.fetchFailed'));
      setQrValue('');
    } finally {
      setQrLoading(false);
    }
  }, [fetchCollectionLicenseText, t]);

  const handleCloseQr = useCallback(() => {
    setQrOpen(false);
  }, []);

  const handleOpenDeleteDialog = useCallback((license: FileTypeLicenseResponse) => {
    setDeleteTarget(license);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deviceId || !deleteTarget?.id) {
      return;
    }

    setDeleting(true);
    try {
      await context.api.api.licenseDeleteDeviceLicense(deviceId, deleteTarget.id);
      await loadLicenses();
      setDeleteTarget(null);
      if (detailLicense?.id === deleteTarget.id) {
        setDetailLicense(null);
      }
    } catch (err) {
      setDownloadError(t('error.fetchFailed'));
    } finally {
      setDeleting(false);
    }
  }, [context.api.api, deleteTarget, detailLicense?.id, deviceId, loadLicenses, t]);

  const handleOpenApproveDialog = useCallback((license: FileTypeLicenseResponse) => {
    setApproveTarget(license);
  }, []);

  const handleCloseApproveDialog = useCallback(() => {
    setApproveTarget(null);
  }, []);

  const handleConfirmApprove = useCallback(async () => {
    if (!approveTarget?.id) {
      return;
    }

    setApproving(true);
    try {
      await context.api.api.licenseApproveDeviceLicense(approveTarget.id);
      await loadLicenses();
      setApproveTarget(null);
      if (detailLicense?.id === approveTarget.id) {
        await loadLicenses();
      }
    } catch (err) {
      setDownloadError(t('error.fetchFailed'));
    } finally {
      setApproving(false);
    }
  }, [context.api.api, approveTarget, detailLicense?.id, loadLicenses, t]);

  const handleOpenRejectDialog = useCallback((license: FileTypeLicenseResponse) => {
    setRejectTarget(license);
    setRejectReason('');
  }, []);

  const handleCloseRejectDialog = useCallback(() => {
    setRejectTarget(null);
    setRejectReason('');
  }, []);

  const handleConfirmReject = useCallback(async () => {
    if (!rejectTarget?.id) {
      return;
    }

    setRejecting(true);
    try {
      await context.api.api.licenseRejectDeviceLicense(rejectTarget.id, {
        rejectionReason: rejectReason.trim() || undefined,
      });
      await loadLicenses();
      setRejectTarget(null);
      setRejectReason('');
      if (detailLicense?.id === rejectTarget.id) {
        await loadLicenses();
      }
    } catch (err) {
      setDownloadError(t('error.fetchFailed'));
    } finally {
      setRejecting(false);
    }
  }, [context.api.api, rejectTarget, rejectReason, detailLicense?.id, loadLicenses, t]);

  const handleOpenSubmitDialog = useCallback((license: FileTypeLicenseResponse) => {
    setSubmitTarget(license);
  }, []);

  const handleCloseSubmitDialog = useCallback(() => {
    setSubmitTarget(null);
  }, []);

  const handleConfirmSubmit = useCallback(async () => {
    if (!deviceId || !submitTarget?.id) {
      return;
    }

    setSubmitting(true);
    try {
      await context.api.api.licenseSubmitDraftLicense(deviceId, submitTarget.id);
      await loadLicenses();
      setSubmitTarget(null);
      if (detailLicense?.id === submitTarget.id) {
        await loadLicenses();
      }
    } catch (err) {
      setDownloadError(t('error.fetchFailed'));
    } finally {
      setSubmitting(false);
    }
  }, [context.api.api, deviceId, submitTarget, detailLicense?.id, loadLicenses, t]);

  const handleOpenSubmitAllDialog = useCallback(() => {
    setSubmitAllOpen(true);
  }, []);

  const handleCloseSubmitAllDialog = useCallback(() => {
    setSubmitAllOpen(false);
  }, []);

  const handleConfirmSubmitAll = useCallback(async () => {
    if (!deviceId) {
      return;
    }

    setSubmittingAll(true);
    try {
      await context.api.api.licenseSubmitAllDraftLicenses(deviceId);
      await loadLicenses();
      setSubmitAllOpen(false);
    } catch (err) {
      setDownloadError(t('error.fetchFailed'));
    } finally {
      setSubmittingAll(false);
    }
  }, [context.api.api, deviceId, loadLicenses, t]);

  const renderOptionInput = (option: FileTypeLicenseOptionResponse) => {
    const optionKey = getOptionKey(option);
    const value = licenseOptionValues[optionKey] ?? '';
    const licenseOption = option.licenseOption;

    if (!licenseOption || !optionKey) {
      return null;
    }

    switch (licenseOption.type) {
      case LicenseOptionType.Boolean:
        const isChecked = value === 'true' || value === '1';
        const isIndeterminate = value === '' || value === undefined;

        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isChecked}
                  indeterminate={isIndeterminate}
                  onChange={(event) => handleOptionValueChange(option, event.target.checked ? 'true' : 'false')}
                />
              }
              label={licenseOption.commonName}
            />
            <Tooltip title={t('deviceManagement.licenses.unset')}>
              <span>
                <IconButton size="small" onClick={() => handleOptionValueChange(option, '')}>
                  <Clear fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      case LicenseOptionType.Number:
        return (
          <TextField
            fullWidth
            required={option.isRequired}
            type="number"
            label={licenseOption.commonName}
            value={value}
            onChange={(event) => handleOptionValueChange(option, event.target.value)}
            helperText={licenseOption.description}
          />
        );
      case LicenseOptionType.Date:
        return (
          <TextField
            fullWidth
            required={option.isRequired}
            type="date"
            label={licenseOption.commonName}
            value={value}
            onChange={(event) => handleOptionValueChange(option, event.target.value)}
            helperText={licenseOption.description}
            InputLabelProps={{ shrink: true }}
          />
        );
      case LicenseOptionType.String:
      default:
        return (
          <TextField
            fullWidth
            required={option.isRequired}
            label={licenseOption.commonName}
            value={value}
            onChange={(event) => handleOptionValueChange(option, event.target.value)}
            helperText={licenseOption.description}
          />
        );
    }
  };

  const licenseColumns = useMemo<GridColDef<FileTypeLicenseResponse>[]>(
    () => [
      {
        field: 'fileTypeName',
        headerName: t('deviceManagement.licensePage.columns.fileType'),
        flex: 1,
        minWidth: 160,
        valueGetter: (_, row) => row.fileTypeName ?? '-',
      },
      {
        field: 'status',
        headerName: t('deviceManagement.licensePage.columns.status'),
        minWidth: 130,
        renderCell: (params) => {
          const effectiveStatus = getEffectiveStatus(params.value, params.row.expiresAt);
          return (
            <Chip
              size="small"
              label={getStatusLabel(effectiveStatus, t)}
              color={getStatusChipColor(effectiveStatus)}
            />
          );
        },
      },
      {
        field: 'issuedAt',
        headerName: t('deviceManagement.licensePage.columns.issuedAt'),
        minWidth: 140,
        valueGetter: (_, row) => formatDate(row.issuedAt),
      },
      {
        field: 'expiresAt',
        headerName: t('deviceManagement.licensePage.columns.expiresAt'),
        minWidth: 140,
        valueGetter: (_, row) => formatDate(row.expiresAt),
      },
      {
        field: 'notes',
        headerName: t('deviceManagement.licensePage.columns.notes'),
        flex: 1,
        minWidth: 160,
        valueGetter: (_, row) => row.notes ?? '-',
      },
      {
        field: 'requestedBy',
        headerName: t('deviceManagement.licensePage.details.submittedBy'),
        minWidth: 150,
        valueGetter: (_, row) => row.requestedBy?.name ?? row.requestedBy?.id ?? '-',
      },
      ...(context.isInternal ? [
        {
          field: 'approvedBy',
          headerName: t('deviceManagement.licensePage.details.approvedBy'),
          minWidth: 150,
          valueGetter: (_, row: FileTypeLicenseResponse) => row.approvedBy?.name ?? row.approvedBy?.id ?? '-',
        } as GridColDef<FileTypeLicenseResponse>
      ] : []),
      {
        field: 'actions',
        headerName: t('deviceManagement.licensePage.columns.actions'),
        minWidth: 160,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={t('deviceManagement.licensePage.actions.details')}>
              <span>
                <IconButton size="small" onClick={() => handleOpenDetails(params.row)}>
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {(() => {
              const effectiveStatus =
                normalizeStatusValue(
                  getEffectiveStatus(params.row.status as FileTypeLicenseStatus, params.row.expiresAt) ?? params.row.status
                ) ?? FileTypeLicenseStatus.Draft;
              const canDelete = context.isInternal && [FileTypeLicenseStatus.Rejected, FileTypeLicenseStatus.Expired].includes(effectiveStatus);
              const isRequested = effectiveStatus === FileTypeLicenseStatus.Requested;
              const isDraft = effectiveStatus === FileTypeLicenseStatus.Draft;
              const isApproved = effectiveStatus === FileTypeLicenseStatus.Approved;
              const isActive = effectiveStatus === FileTypeLicenseStatus.Active;
              const isOfficeOrHigher = (context.userData?.userGroup ?? EUserGroup.CUSTOMER) <= EUserGroup.OFFICE;
              
              if (isDraft) {
                return (
                  <>
                    <Tooltip title={t('deviceManagement.licensePage.actions.submit')}>
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenSubmitDialog(params.row)}
                        >
                          <ShoppingCart fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('deviceManagement.licensePage.actions.delete')}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(params.row)}
                        >
                          <DeleteForever fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                );
              }
              
              if (isRequested && context.isInternal) {
                return (
                  <>
                    <Tooltip title={t('deviceManagement.licensePage.actions.approve')}>
                      <span>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleOpenApproveDialog(params.row)}
                        >
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('deviceManagement.licensePage.actions.reject')}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenRejectDialog(params.row)}
                        >
                          <Cancel fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                );
              }
              
              if (canDelete) {
                return (
                  <Tooltip title={t('deviceManagement.licensePage.actions.delete')}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(params.row)}
                      >
                        <DeleteForever fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              }
              
              if ((isApproved || isActive) && isOfficeOrHigher) {
                return (
                  <Tooltip title={t('deviceManagement.licensePage.actions.revoke')}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenRevokeDialog(params.row)}
                      >
                        <RemoveCircleOutline fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              }
              
              return null;
            })()}
          </Stack>
        ),
      },
    ],
    [handleOpenDetails, handleOpenDeleteDialog, handleOpenRevokeDialog, handleOpenApproveDialog, handleOpenRejectDialog, handleOpenSubmitDialog, context.isInternal, t],
  );

  const licenseDetails = detailLicense?.licenseData;
  const licenseModules = licenseDetails?.m ?? [];
  const licenseMeta = licenseDetails?.x ?? {};
  const issuedUnix = licenseDetails?.iss;
  useEffect(() => {
    if (!collectionOpen) {
      return;
    }

    loadCollection();
  }, [collectionOpen, loadCollection]);

  if (accessDenied) {
    return (
      <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
        <AccessDenied
          titleKey="deviceManagement.licensePage.accessDeniedTitle"
          messageKey="deviceManagement.licensePage.accessDeniedMessage"
        />
      </Container>
    );
  }

  if (deviceNotFound) {
    return <NotFound />;
  }

  return (
    <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack spacing={0.5}>
            <Typography variant="h4" component="h1">
              {t('deviceManagement.licensePage.title')}
            </Typography>
            {deviceId && (
              <Typography variant="body2" color="text.secondary">
                {t('deviceManagement.columns.deviceId')}: {deviceId}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip
              title={
                downloadingCollection
                  ? t('deviceManagement.licensePage.actions.downloading')
                  : t('deviceManagement.licensePage.collection.download')
              }
            >
              <span>
                <IconButton
                  color="primary"
                  onClick={handleDownloadCollection}
                  disabled={downloadingCollection || !deviceId}
                >
                  <Download />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('deviceManagement.licensePage.collection.qrTitle')}>
              <span>
                <IconButton color="primary" onClick={handleOpenQr} disabled={!deviceId}>
                  <QrCode2 />
                </IconButton>
              </span>
            </Tooltip>
            {hasLicenseXml && (
              <Tooltip title={t('deviceManagement.licensePage.xml.title')}>
                <span>
                  <IconButton color="primary" onClick={handleOpenXmlDialog} disabled={!deviceId}>
                    <FontAwesomeIcon icon={faFileCode} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
              {t('deviceManagement.licensePage.back')}
            </Button>
          </Stack>
        </Stack>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">{t('deviceManagement.deviceDetails.title')}</Typography>
              {deviceDetailsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : deviceDetails ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.name')}
                    </Typography>
                    <Typography variant="body1">{deviceDetails.name ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.deviceId')}
                    </Typography>
                    <Typography variant="body1">{deviceDetails.deviceId ?? deviceDetails.id ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.hostSystemName')}
                    </Typography>
                    <Typography variant="body1">{deviceDetails.hostSystemName ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.osVersion')}
                    </Typography>
                    <Typography variant="body1">{deviceDetails.osVersion ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.supportCode')}
                    </Typography>
                    {deviceDetails.supportCode ? (
                      <MuiLink
                        component={Link}
                        to={`/deviceManagement/${deviceDetails.supportCode}`}
                        underline="hover"
                        sx={{ fontFamily: 'monospace', fontSize: '0.95rem' }}
                      >
                        {deviceDetails.supportCode}
                      </MuiLink>
                    ) : (
                      <Typography variant="body1">-</Typography>
                    )}
                  </Grid>
                  {deviceDetails.customerName && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        {t('deviceManagement.columns.customerName')}
                      </Typography>
                      {deviceDetails.customerId ? (
                        <MuiLink
                          component={Link}
                          to={`/admin/support-contracts?customerId=${deviceDetails.customerId}`}
                          underline="hover"
                        >
                          {deviceDetails.customerName}
                        </MuiLink>
                      ) : (
                        <Typography variant="body1">{deviceDetails.customerName}</Typography>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.deviceStatus')}
                    </Typography>
                    <Typography variant="body1">
                      {deviceDetails.deviceStatus ? t(`deviceManagement.status.${deviceDetails.deviceStatus}`) : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.updatesInfo')}
                    </Typography>
                    <Typography variant="body1">
                      {getUpdatesInfoLabel(deviceDetails.deviceStatus, deviceDetails.updatesMonths, deviceDetails.registrationDate)}
                    </Typography>
                  </Grid>
                  {deviceDetails.licenseCode && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        {t('deviceManagement.columns.licenseCode')}
                      </Typography>
                      <Typography variant="body1">{deviceDetails.licenseCode}</Typography>
                    </Grid>
                  )}
                  
                  {/* HostID Validation Results */}
                  {deviceDetails.deviceId && deviceDetails.deviceId.length >= 5 && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        {t('hostIdCheck.title')}
                      </Typography>
                      {hostIdCheckLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" color="text.secondary">
                            {t('hostIdCheck.checking')}
                          </Typography>
                        </Box>
                      ) : hostIdCheckError ? (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {hostIdCheckError}
                        </Alert>
                      ) : hostIdCheckData ? (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              {t('hostIdCheck.checksumValid')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {hostIdCheckData.checksum_valid ? (
                                <CheckCircle color="success" fontSize="small" />
                              ) : (
                                <Cancel color="error" fontSize="small" />
                              )}
                              <Typography variant="body1">
                                {hostIdCheckData.checksum_valid ? t('yes') : t('no')}
                              </Typography>
                            </Box>
                          </Grid>
                          {hostIdCheckData.os_type && (
                            <Grid item xs={12} md={3}>
                              <Typography variant="body2" color="text.secondary">
                                {t('hostIdCheck.osType')}
                              </Typography>
                              <Typography variant="body1">{hostIdCheckData.os_type}</Typography>
                            </Grid>
                          )}
                          {hostIdCheckData.identifier_source && (
                            <Grid item xs={12} md={3}>
                              <Typography variant="body2" color="text.secondary">
                                {t('hostIdCheck.identifierSource')}
                              </Typography>
                              <Typography variant="body1">{hostIdCheckData.identifier_source}</Typography>
                            </Grid>
                          )}
                          {hostIdCheckData.execution_context && (
                            <Grid item xs={12} md={3}>
                              <Typography variant="body2" color="text.secondary">
                                {t('hostIdCheck.executionContext')}
                              </Typography>
                              <Typography variant="body1">{hostIdCheckData.execution_context}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      ) : null}
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography color="text.secondary">-</Typography>
              )}
              {deviceDetailsError && <Alert severity="error">{deviceDetailsError}</Alert>}
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">{t('deviceManagement.licensePage.assignedTitle')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('deviceManagement.licensePage.assignedDescription')}
              </Typography>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                {!context.isInternal && licenses.some(l => l.status === FileTypeLicenseStatus.Draft) && (
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<ShoppingCart />}
                    onClick={handleOpenSubmitAllDialog}
                  >
                    {t('deviceManagement.licensePage.actions.submitAll')}
                  </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" onClick={handleOpenCollection} disabled={licenses.length === 0}>
                  {t('deviceManagement.licensePage.collection.open')}
                </Button>
              </Box>
              {licensesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : licenses.length === 0 ? (
                <Typography color="text.secondary">
                  {t('deviceManagement.licensePage.noLicenses')}
                </Typography>
              ) : (
                <DataGrid
                  autoHeight
                  rows={licenses}
                  columns={licenseColumns}
                  getRowId={(row) => row.id ?? `${row.fileTypeId}-${row.issuedAt}`}
                  disableRowSelectionOnClick
                  hideFooter
                />
              )}
              {licensesError && <Alert severity="error">{licensesError}</Alert>}
              {downloadError && <Alert severity="error">{downloadError}</Alert>}
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">{t('deviceManagement.manageLicenses')}</Typography>
                <Button
                  variant="outlined"
                  startIcon={<FontAwesomeIcon icon={faUpload} />}
                  onClick={triggerFileUpload}
                  disabled={!deviceId}
                >
                  {t('deviceManagement.licensePage.upload.button')}
                </Button>
              </Box>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".xml"
                style={{ display: 'none' }}
              />
              <Alert severity="info">
                <Typography variant="body2">
                  {context.isInternal 
                    ? t('deviceManagement.licenses.infoText') 
                    : t('deviceManagement.licenses.infoTextCustomer')}
                </Typography>
              </Alert>

              <Autocomplete
                options={availableFileTypes}
                getOptionLabel={(option) => option.name || option.id || '-'}
                value={selectedFileType}
                onChange={(event, newValue) => {
                  handleFileTypeChange(newValue?.id ?? '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('deviceManagement.licenses.selectFileType')}
                    placeholder={t('deviceManagement.licenses.searchFileType')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {fileTypesLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={option.id} {...otherProps}>
                      <Box>
                        <Typography variant="body1">
                          {option.name}
                        </Typography>
                        {option.description && (
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
                noOptionsText={fileTypesLoading ? t('loading') : t('deviceManagement.licenses.noFileTypes')}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={fileTypesLoading}
                autoHighlight
                clearOnBlur
                selectOnFocus
              />

              {licenseOptionsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label={t('deviceManagement.licenses.expiresAt')}
                    value={assignExpiresAt}
                    onChange={(event) => setAssignExpiresAt(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('deviceManagement.licenses.notes')}
                    value={assignNotes}
                    onChange={(event) => setAssignNotes(event.target.value)}
                  />
                </Grid>
              </Grid>

              {!licenseOptionsLoading && selectedFileType && licenseOptions.length === 0 && (
                <Alert severity="warning">{t('deviceManagement.licenses.noOptions')}</Alert>
              )}

              {!licenseOptionsLoading && selectedFileType && licenseOptions.length > 0 && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('deviceManagement.licenses.licenseOptions')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {t('deviceManagement.licenses.licenseOptionsDescription')}
                    </Typography>
                    <Grid container spacing={2}>
                      {licenseOptions.map((option) => (
                        <Grid item xs={12} md={6} key={option.id ?? getOptionKey(option)}>
                          {renderOptionInput(option)}
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {assignError && <Alert severity="error">{assignError}</Alert>}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAssignLicense}
                  disabled={!selectedFileType || assigning || licenseOptionsLoading}
                >
                  {assigning 
                    ? (context.isInternal ? t('deviceManagement.licenses.assigning') : t('deviceManagement.licenses.creatingDraft'))
                    : (context.isInternal ? t('deviceManagement.licenses.assignLicense') : t('deviceManagement.licenses.createDraft'))}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={Boolean(revokeTarget)} onClose={handleCloseRevokeDialog}>
        <DialogTitle>{t('deviceManagement.licensePage.confirmRevoke.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('deviceManagement.licensePage.confirmRevoke.description')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRevokeDialog} color="secondary" disabled={revoking}>
            {t('deviceManagement.licenses.cancel')}
          </Button>
          <Button onClick={handleConfirmRevoke} color="error" variant="contained" disabled={revoking}>
            {revoking ? t('deviceManagement.licensePage.actions.revoking') : t('deviceManagement.licensePage.actions.revoke')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(detailLicense)} onClose={handleCloseDetails} fullWidth maxWidth="md">
        <DialogTitle>{t('deviceManagement.licensePage.details.title')}</DialogTitle>
        <DialogContent>
          {!detailLicense ? null : (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle1">{t('deviceManagement.licensePage.details.basic')}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.licensePage.columns.fileType')}
                    </Typography>
                    <Typography variant="body1">{detailLicense.fileTypeName ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.licensePage.columns.status')}
                    </Typography>
                    <Typography variant="body1">{getStatusLabel(detailLicense.status, t)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.licensePage.columns.issuedAt')}
                    </Typography>
                    <Typography variant="body1">{formatDate(detailLicense.issuedAt)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.licensePage.columns.expiresAt')}
                    </Typography>
                    <Typography variant="body1">{formatDate(detailLicense.expiresAt)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.licensePage.columns.notes')}
                    </Typography>
                    <Typography variant="body1">{detailLicense.notes ?? '-'}</Typography>
                  </Grid>
                  {detailLicense.requestedBy && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('deviceManagement.licensePage.details.submittedBy')}
                      </Typography>
                      <Typography variant="body1">{detailLicense.requestedBy.name ?? detailLicense.requestedBy.id}</Typography>
                    </Grid>
                  )}
                  {context.isInternal && detailLicense.approvedBy && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('deviceManagement.licensePage.details.approvedBy')}
                      </Typography>
                      <Typography variant="body1">{detailLicense.approvedBy.name ?? detailLicense.approvedBy.id}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1">{t('deviceManagement.licensePage.details.licenseData')}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.licensePage.details.licenseId')}
                    </Typography>
                    <Typography variant="body1">{licenseDetails?.id ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.licensePage.details.licensee')}
                    </Typography>
                    <Typography variant="body1">{licenseDetails?.n ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.licensePage.details.hardwareId')}
                    </Typography>
                    <Typography variant="body1">{licenseDetails?.hw ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.licensePage.details.issuedUnix')}
                    </Typography>
                    <Typography variant="body1">
                      {issuedUnix ? dayjs.unix(issuedUnix).format('DD.MM.YYYY') : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1">{t('deviceManagement.licensePage.details.modules')}</Typography>
                {licenseModules.length === 0 ? (
                  <Typography color="text.secondary">{t('deviceManagement.licensePage.details.noModules')}</Typography>
                ) : (
                  <Stack spacing={2}>
                    {licenseModules.map((module, index) => (
                      <Paper key={`${module.c ?? 'module'}-${index}`} variant="outlined" sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                              {t('deviceManagement.licensePage.details.moduleCode')}
                            </Typography>
                            <Typography variant="body1">{module.c ?? '-'}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              {t('deviceManagement.licensePage.details.moduleData')}
                            </Typography>
                            <Typography variant="body1" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                              {module.d ? JSON.stringify(module.d, null, 2) : '-'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1">{t('deviceManagement.licensePage.details.metadata')}</Typography>
                {Object.keys(licenseMeta).length === 0 ? (
                  <Typography color="text.secondary">{t('deviceManagement.licensePage.details.noMetadata')}</Typography>
                ) : (
                  <Grid container spacing={2}>
                    {Object.entries(licenseMeta).map(([key, value]) => (
                      <Grid item xs={12} md={6} key={key}>
                        <Typography variant="body2" color="text.secondary">
                          {key}
                        </Typography>
                        <Typography variant="body1">{value}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Stack>
              {downloadError && <Alert severity="error">{downloadError}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {detailLicense && (
            <Tooltip
              title={
                downloadingCollection
                  ? t('deviceManagement.licensePage.actions.downloading')
                  : t('deviceManagement.licensePage.collection.download')
              }
            >
              <span>
                <IconButton
                  onClick={handleDownloadCollection}
                  color="primary"
                  disabled={downloadingCollection || !deviceId}
                >
                  <Download />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {detailLicense && (() => {
            const effectiveStatus =
              normalizeStatusValue(
                getEffectiveStatus(detailLicense.status as FileTypeLicenseStatus, detailLicense.expiresAt) ??
                  detailLicense.status
              ) ?? FileTypeLicenseStatus.Draft;
            const canDelete = context.isInternal && [FileTypeLicenseStatus.Rejected, FileTypeLicenseStatus.Expired].includes(effectiveStatus);
            const isRequested = effectiveStatus === FileTypeLicenseStatus.Requested;
            const isApproved = effectiveStatus === FileTypeLicenseStatus.Approved;
            
            if (isRequested) {
              return (
                <>
                  <Tooltip title={t('deviceManagement.licensePage.actions.approve')}>
                    <span>
                      <IconButton
                        onClick={() => handleOpenApproveDialog(detailLicense)}
                        color="success"
                        disabled={approving}
                      >
                        <CheckCircle />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('deviceManagement.licensePage.actions.reject')}>
                    <span>
                      <IconButton
                        onClick={() => handleOpenRejectDialog(detailLicense)}
                        color="error"
                        disabled={rejecting}
                      >
                        <Cancel />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              );
            }
            
            return canDelete ? (
              <Tooltip title={t('deviceManagement.licensePage.actions.delete')}>
                <span>
                  <IconButton
                    onClick={() => handleOpenDeleteDialog(detailLicense)}
                    color="error"
                    disabled={deleting}
                  >
                    <DeleteForever />
                  </IconButton>
                </span>
              </Tooltip>
            ) : !isApproved ? (
              <Tooltip title={t('deviceManagement.licensePage.actions.revoke')}>
                <span>
                  <IconButton
                    onClick={() => handleOpenRevokeDialog(detailLicense)}
                    color="error"
                    disabled={detailLicense.status === FileTypeLicenseStatus.Rejected}
                  >
                    <RemoveCircleOutline />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null;
          })()}
          <Tooltip title={t('deviceManagement.licensePage.actions.close')}>
            <IconButton onClick={handleCloseDetails} color="primary">
              <Close />
            </IconButton>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={handleCloseDeleteDialog}>
        <DialogTitle>{t('deviceManagement.licensePage.confirmDelete.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('deviceManagement.licensePage.confirmDelete.description')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="secondary" disabled={deleting}>
            {t('deviceManagement.licenses.cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting
              ? t('deviceManagement.licensePage.actions.deleting')
              : t('deviceManagement.licensePage.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(approveTarget)} onClose={handleCloseApproveDialog}>
        <DialogTitle>{t('deviceManagement.licensePage.confirmApprove.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('deviceManagement.licensePage.confirmApprove.description')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApproveDialog} color="secondary" disabled={approving}>
            {t('deviceManagement.licenses.cancel')}
          </Button>
          <Button onClick={handleConfirmApprove} color="success" variant="contained" disabled={approving}>
            {approving
              ? t('deviceManagement.licensePage.actions.approving')
              : t('deviceManagement.licensePage.actions.approve')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(rejectTarget)} onClose={handleCloseRejectDialog}>
        <DialogTitle>{t('deviceManagement.licensePage.confirmReject.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>{t('deviceManagement.licensePage.confirmReject.description')}</Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('deviceManagement.licensePage.confirmReject.reason')}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t('deviceManagement.licensePage.confirmReject.reasonPlaceholder')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectDialog} color="secondary" disabled={rejecting}>
            {t('deviceManagement.licenses.cancel')}
          </Button>
          <Button onClick={handleConfirmReject} color="error" variant="contained" disabled={rejecting}>
            {rejecting
              ? t('deviceManagement.licensePage.actions.rejecting')
              : t('deviceManagement.licensePage.actions.reject')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(submitTarget)} onClose={handleCloseSubmitDialog}>
        <DialogTitle>{t('deviceManagement.licensePage.confirmSubmit.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('deviceManagement.licensePage.confirmSubmit.description')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubmitDialog} color="secondary" disabled={submitting}>
            {t('deviceManagement.licenses.cancel')}
          </Button>
          <Button onClick={handleConfirmSubmit} color="primary" variant="contained" disabled={submitting}>
            {submitting
              ? t('deviceManagement.licensePage.actions.submitting')
              : t('deviceManagement.licensePage.actions.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={submitAllOpen} onClose={handleCloseSubmitAllDialog}>
        <DialogTitle>{t('deviceManagement.licensePage.confirmSubmitAll.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('deviceManagement.licensePage.confirmSubmitAll.description')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubmitAllDialog} color="secondary" disabled={submittingAll}>
            {t('deviceManagement.licenses.cancel')}
          </Button>
          <Button onClick={handleConfirmSubmitAll} color="primary" variant="contained" disabled={submittingAll}>
            {submittingAll
              ? t('deviceManagement.licensePage.actions.submittingAll')
              : t('deviceManagement.licensePage.actions.submitAll')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={collectionOpen} onClose={handleCloseCollection} fullWidth maxWidth="md">
        <DialogTitle>{t('deviceManagement.licensePage.collection.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('deviceManagement.licensePage.collection.description')}
            </Typography>
            {collectionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : (collectionData?.modules?.length ?? 0) === 0 ? (
              <Typography color="text.secondary">{t('deviceManagement.licensePage.noLicenses')}</Typography>
            ) : (
              <Stack spacing={3}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1">{t('deviceManagement.licensePage.details.basic')}</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t('deviceManagement.columns.name')}
                        </Typography>
                        <Typography variant="body1">{collectionData?.deviceName ?? '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t('deviceManagement.columns.deviceId')}
                        </Typography>
                        <Typography variant="body1">{collectionData?.deviceId ?? '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t('deviceManagement.licensePage.details.hardwareId')}
                        </Typography>
                        <Typography variant="body1">{collectionData?.deviceHardwareId ?? '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t('deviceManagement.licensePage.collection.serviceUnitsTotal')}
                        </Typography>
                        <Typography variant="body1">
                          {collectionData?.serviceEinheitenTotal ?? '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Stack>
                </Paper>

                {collectionData?.modules?.map((module) => {
                  const moduleKey = module.fileTypeId ?? module.fileTypeName ?? 'unknown';
                  const moduleOptions = module.options ?? [];
                  const licenses = module.licenses ?? [];

                  return (
                    <Paper key={moduleKey} variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          justifyContent="space-between"
                        >
                          <Box>
                            <Typography variant="subtitle1">{module.fileTypeName ?? moduleKey}</Typography>
                            {module.packageId && (
                              <Typography variant="body2" color="text.secondary">
                                {t('deviceManagement.licensePage.collection.packageId')}: {module.packageId}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            size="small"
                            label={`${licenses.length} ${t('deviceManagement.licensePage.collection.parts')}`}
                            variant="outlined"
                          />
                        </Stack>

                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('deviceManagement.licensePage.collection.optionsTitle')}
                          </Typography>
                          {moduleOptions.length === 0 ? (
                            <Typography>{t('deviceManagement.licensePage.collection.optionsEmpty')}</Typography>
                          ) : (
                            <Grid container spacing={2}>
                              {moduleOptions.map((option) => {
                                const optionValue =
                                  (option as { value?: unknown }).value ?? "-";
                                return (
                                <Grid item xs={12} md={6} key={option.identifier ?? option.commonName}>
                                  <Typography variant="body2" color="text.secondary">
                                    {option.commonName ?? option.identifier ?? '-'}
                                  </Typography>
                                  <Typography variant="body1">
                                    {formatOptionValueLabel(optionValue)}
                                  </Typography>
                                  {(option.serviceEinheiten != null || option.serviceEinheitenTotal != null) && (
                                    <Typography variant="caption" color="text.secondary">
                                      {option.serviceEinheiten != null && (
                                        <>
                                          {t('deviceManagement.licensePage.collection.serviceUnits')}: {option.serviceEinheiten}
                                        </>
                                      )}
                                      {option.serviceEinheiten != null && option.serviceEinheitenTotal != null ? ' · ' : ''}
                                      {option.serviceEinheitenTotal != null && (
                                        <>
                                          {t('deviceManagement.licensePage.collection.serviceUnitsTotal')}: {option.serviceEinheitenTotal}
                                        </>
                                      )}
                                    </Typography>
                                  )}
                                </Grid>
                              );
                              })}
                            </Grid>
                          )}
                        </Stack>

                        <Divider />

                        <Stack spacing={2}>
                          <Typography variant="body2" color="text.secondary">
                            {t('deviceManagement.licensePage.collection.parts')}
                          </Typography>
                          {licenses.map((part) => {
                            const partOptions = part.options ?? {};
                            return (
                              <Accordion
                                key={part.licenseId ?? `${moduleKey}-${part.issuedAt}`}
                                variant="outlined"
                                disableGutters
                                sx={{ '&:before': { display: 'none' } }}
                              >
                                <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
                                  <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                    sx={{ width: '100%' }}
                                  >
                                    <Typography variant="subtitle2">
                                      {t('deviceManagement.licensePage.details.licenseId')}: {part.licenseId ?? '-'}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={getStatusLabel(getEffectiveStatus(part.status, part.expiresAt), t)}
                                      color={getStatusChipColor(getEffectiveStatus(part.status, part.expiresAt))}
                                    />
                                    <Typography variant="body2" color="text.secondary">
                                      {t('deviceManagement.licensePage.columns.issuedAt')}: {formatDate(part.issuedAt)}
                                    </Typography>
                                  </Stack>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Stack spacing={2}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} md={4}>
                                        <Typography variant="body2" color="text.secondary">
                                          {t('deviceManagement.licensePage.details.licenseId')}
                                        </Typography>
                                        <Typography variant="body1">{part.licenseId ?? '-'}</Typography>
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <Typography variant="body2" color="text.secondary">
                                          {t('deviceManagement.licensePage.columns.status')}
                                        </Typography>
                                        <Chip
                                          size="small"
                                          label={getStatusLabel(getEffectiveStatus(part.status, part.expiresAt), t)}
                                          color={getStatusChipColor(getEffectiveStatus(part.status, part.expiresAt))}
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <Typography variant="body2" color="text.secondary">
                                          {t('deviceManagement.licensePage.columns.issuedAt')}
                                        </Typography>
                                        <Typography variant="body1">{formatDate(part.issuedAt)}</Typography>
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <Typography variant="body2" color="text.secondary">
                                          {t('deviceManagement.licensePage.columns.expiresAt')}
                                        </Typography>
                                        <Typography variant="body1">{formatDate(part.expiresAt)}</Typography>
                                      </Grid>
                                      <Grid item xs={12} md={8}>
                                        <Typography variant="body2" color="text.secondary">
                                          {t('deviceManagement.licensePage.columns.notes')}
                                        </Typography>
                                        <Typography variant="body1">{part.notes ?? '-'}</Typography>
                                      </Grid>
                                    </Grid>
                                    <Divider />
                                    <Stack spacing={1}>
                                      <Typography variant="body2" color="text.secondary">
                                        {t('deviceManagement.licensePage.collection.partOptions')}
                                      </Typography>
                                      {Object.keys(partOptions).length === 0 ? (
                                        <Typography>{t('deviceManagement.licensePage.collection.optionsEmpty')}</Typography>
                                      ) : (
                                        <Grid container spacing={2}>
                                          {Object.entries(partOptions).map(([key, value]) => (
                                            <Grid item xs={12} md={6} key={key}>
                                              <Typography variant="body2" color="text.secondary">
                                                {resolveOptionLabel(key, moduleOptions)}
                                              </Typography>
                                              <Typography variant="body1">{formatOptionValueLabel(value)}</Typography>
                                            </Grid>
                                          ))}
                                        </Grid>
                                      )}
                                    </Stack>
                                  </Stack>
                                </AccordionDetails>
                              </Accordion>
                            );
                          })}
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
            {collectionError && <Alert severity="error">{collectionError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCollection} color="primary">
            {t('deviceManagement.licensePage.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={qrOpen} onClose={handleCloseQr} fullWidth maxWidth="sm">
        <DialogTitle>{t('deviceManagement.licensePage.collection.qrTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, alignItems: 'center' }}>
            {qrLoading ? (
              <CircularProgress />
            ) : qrValue ? (
              <QRCode value={qrValue} size={256} level="M" />
            ) : (
              <Typography color="text.secondary">
                {t('deviceManagement.licensePage.collection.qrEmpty')}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" align="center">
              {t('deviceManagement.licensePage.collection.qrInfo')}
            </Typography>
            {qrError && <Alert severity="error">{qrError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadCollection} color="primary" disabled={downloadingCollection || !deviceId}>
            {t('deviceManagement.licensePage.collection.download')}
          </Button>
          <Button onClick={handleCloseQr} color="primary">
            {t('deviceManagement.licensePage.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={xmlDialogOpen} 
        onClose={handleCloseXmlDialog} 
        fullWidth 
        maxWidth="xl"
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>{t('deviceManagement.licensePage.xml.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('deviceManagement.licensePage.xml.description')}
            </Typography>
          </Box>
          {xmlLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : xmlContent ? (
            <Box sx={{ flex: 1, minHeight: 0, border: '1px solid', borderColor: 'divider', mx: 2, mb: 2 }}>
              <Editor
                height="100%"
                defaultLanguage="xml"
                value={xmlContent}
                options={{
                  readOnly: true,
                  minimap: { enabled: true },
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  wordWrap: 'off',
                  automaticLayout: true,
                  scrollbar: {
                    horizontal: 'visible',
                    vertical: 'visible',
                    horizontalScrollbarSize: 10,
                    verticalScrollbarSize: 10,
                  },
                }}
                theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'vs'}
              />
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography color="text.secondary">
                {t('deviceManagement.licensePage.xml.empty')}
              </Typography>
            </Box>
          )}
          {xmlError && (
            <Box sx={{ px: 2, pb: 2 }}>
              <Alert severity="error">{xmlError}</Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadXml} color="primary" disabled={!xmlContent} startIcon={<Download />}>
            {t('deviceManagement.licensePage.xml.download')}
          </Button>
          <Button onClick={handleCloseXmlDialog} color="primary">
            {t('deviceManagement.licensePage.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

const DeviceLicensePage: React.FC = () => {
  return <DeviceLicensePageContent />;
};

export default DeviceLicensePage;
